import {
  ArrowRight, Banknote, FileSignature, FileText, Package, Receipt, ReceiptText,
  Search, ShoppingCart, Truck, Users,
} from 'lucide-react';
import { type ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NAV } from '../../app/AppLayout.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { moduleEnabled, SECTION_TO_MODULE } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';
import { CATALOG as QUICK_ACTIONS } from '../dashboard/QuickActions.js';

type Icon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

interface Cmd {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  icon: Icon;
  to: string;
}

interface PartyLite { id: string; code: string; displayName: string; partyType?: string }
interface ItemLite { id: string; code: string; name: string; barcode?: string | null }
/** Every document type answers the shared list shape, so one row type covers all of them. */
interface DocLite {
  id: string;
  docNumber: string;
  isCreditNote?: boolean | 0 | 1;
  isDebitNote?: boolean | 0 | 1;
  /** Resolved server-side — whose document this is, so the row is readable. */
  partyName?: string | null;
  total?: string;
  amount?: string;
  currencyCode?: string;
}
interface Records { parties: PartyLite[] }
/** What the SERVER matched for the current query, per source key. */
type Hits = { items: ItemLite[] } & Record<string, DocLite[] | ItemLite[] | undefined>;

/** How many of each kind the palette asks for. Small: this is a jump list. */
const HIT_LIMIT = 5;

/**
 * Everything the palette can search, and how.
 *
 * All of these are DOCUMENTS — they share one list endpoint shape and, since
 * `?q=` landed on it, one search. Each is gated exactly as its page is, so the
 * palette can never surface a record its reader could not open, and a type the
 * tenant has switched off simply isn't queried.
 */
const DOC_SOURCES = [
  { key: 'invoices', endpoint: '/invoices', perm: 'invoices.read', base: '/sales/invoices', icon: Receipt, labelKey: 'palette.invoices', fallback: 'Invoices' },
  { key: 'bills', endpoint: '/bills', perm: 'bills.read', base: '/purchasing/bills', icon: ReceiptText, labelKey: 'palette.bills', fallback: 'Purchase invoices' },
  { key: 'orders', endpoint: '/sales-orders', perm: 'sales_orders.read', base: '/sales/orders', icon: ShoppingCart, labelKey: 'palette.orders', fallback: 'Orders' },
  { key: 'quotes', endpoint: '/quotations', perm: 'quotations.read', module: 'quotations', base: '/quotations', icon: FileSignature, labelKey: 'palette.quotes', fallback: 'Quotations' },
  // Payments have no detail page of their own — the voucher IS the document.
  { key: 'payments', endpoint: '/payments', perm: 'payments.read', base: '/payments', print: true, icon: Banknote, labelKey: 'palette.payments', fallback: 'Payments' },
] as const;

type ScopeKey = 'all' | 'pages' | 'customers' | 'suppliers' | 'items' | (typeof DOC_SOURCES)[number]['key'];

const matches = (q: string, ...hay: Array<string | null | undefined>): boolean =>
  hay.some((h) => (h ?? '').toLowerCase().includes(q));

/**
 * Global ⌘K / Ctrl+K command palette: fuzzy-jump to any page, run a quick
 * action, or search customers / items / invoices. Per Emil, a palette is used
 * dozens of times a day, so it opens INSTANTLY (no entrance animation).
 *
 * Two different search strategies, on purpose. The customer book is paged in
 * full on first open and matched in memory, so the very first keystroke
 * answers without a round trip. Items and invoices are matched by the SERVER
 * per query — there are far too many of either to hold, and holding one page
 * of them is what made this box answer "no results" for almost everything.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  const permissions = useAuthStore((s) => s.permissions);
  const enabledModules = useEnabledModules(tenant?.id ?? null);

  const [query, setQuery] = useState('');
  /** Which kind of record the search is narrowed to. 'all' = everything. */
  const [scope, setScope] = useState<ScopeKey>('all');
  const [active, setActive] = useState(0);
  const [records, setRecords] = useState<Records | null>(null);
  const [hits, setHits] = useState<Hits | null>(null);
  /** Guards a server answer against a newer keystroke overtaking it. */
  const hitSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const can = (perm?: string): boolean => !perm || permissions.includes(perm);
  const modOn = (m?: string): boolean => !m || moduleEnabled(enabledModules, m as never);

  // Global ⌘K / Ctrl+K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Reset + focus on open; lazily load searchable records once.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setScope('all');
    setActive(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    if (!records && !loadingRef.current) {
      loadingRef.current = true;
      // Customers are held in full and matched here: the book is bounded, it
      // is paged once per session, and having it in memory keeps the first
      // keystroke instant. Items and invoices are NOT — see searchServer.
      void fetchAllParties<PartyLite>()
        .then((parties) => setRecords({ parties }))
        .catch(() => setRecords({ parties: [] }));
    }
  }, [open, records]);

  /**
   * Items and invoices are matched by the SERVER, per query.
   *
   * They used to be preloaded like the customers — one page each — and the
   * palette then matched against that. On a real shop that is the first 200
   * items of four thousand and the newest 200 invoices, so almost everything
   * came back "no results": the box looked broken rather than empty. Worse for
   * invoices, which were matched on the document NUMBER alone, so the way a
   * shop actually looks one up — by whose it is — could not work at all.
   *
   * Debounced, and from two characters: one keystroke fires a query per letter
   * for a result nobody can read yet. A sequence number drops an answer that
   * arrives after a newer one (typing outruns the network).
   */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits(null); return undefined; }
    const seq = ++hitSeq.current;
    const timer = window.setTimeout(() => {
      // Only the sources this scope covers, and only the ones this reader may
      // open — a type they have no permission for would 403 on every keystroke.
      const sources = DOC_SOURCES.filter((s) =>
        (scope === 'all' || scope === s.key)
        && can(s.perm)
        && modOn('module' in s ? s.module : undefined));
      const wantItems = (scope === 'all' || scope === 'items') && can('items.read');
      const calls: Array<Promise<[string, DocLite[] | ItemLite[]]>> = [];
      if (wantItems) {
        calls.push(api().get<{ items: ItemLite[] }>('/items', { params: { q, limit: HIT_LIMIT } })
          .then((r) => ['items', r.data.items] as [string, ItemLite[]]));
      }
      for (const s of sources) {
        calls.push(api().get<{ items: DocLite[] }>(s.endpoint, { params: { q, limit: HIT_LIMIT } })
          .then((r) => [s.key, r.data.items] as [string, DocLite[]]));
      }
      void Promise.allSettled(calls).then((results) => {
        if (seq !== hitSeq.current) return; // superseded by a later keystroke
        const next: Hits = { items: [] };
        for (const r of results) {
          // A rejected source is simply a source with nothing to say — one
          // type being unavailable must never blank the whole palette.
          if (r.status !== 'fulfilled') continue;
          const [key, rows] = r.value;
          next[key] = rows;
        }
        setHits(next);
      });
    }, 220);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, scope, permissions, enabledModules]);

  const commands = useMemo<Cmd[]>(() => {
    const q = query.trim().toLowerCase();
    const out: Cmd[] = [];

    // Pages + actions — client-side, they are a fixed list.
    if (scope === 'all' || scope === 'pages') {
      for (const section of NAV) {
        const secMod = SECTION_TO_MODULE[section.labelKey];
        if (secMod && !moduleEnabled(enabledModules, secMod)) continue;
        const secLabel = t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() });
        for (const item of section.items) {
          // `anyPerm`: a merged page (invoices + orders) is reachable on either
          // half's permission, and the palette must agree with the sidebar or
          // one of them is lying about what this user can open.
          if (!can(item.perm) || !modOn(item.module)) continue;
          if (item.anyPerm && !item.anyPerm.some((p) => permissions.includes(p))) continue;
          const label = t(item.labelKey, { defaultValue: item.labelKey });
          // Match the route path too, so English queries find pages even when the
          // UI is in Arabic (e.g. "inv" → /sales/invoices).
          if (q && !matches(q, label, secLabel, item.to)) continue;
          out.push({ id: `page:${item.to}`, group: t('palette.pages', { defaultValue: 'Pages' }), label, sublabel: secLabel, icon: item.icon, to: item.to });
        }
      }
      for (const a of QUICK_ACTIONS) {
        if (!can(a.perm) || !modOn(a.module)) continue;
        const label = t(a.labelKey, { defaultValue: a.defaultLabel });
        if (q && !matches(q, label, a.defaultLabel, a.to, a.key)) continue;
        out.push({ id: `action:${a.key}`, group: t('palette.actions', { defaultValue: 'Actions' }), label, icon: a.icon, to: a.to });
      }
    }

    // Parties — matched here, from the book held in memory. Split by side so a
    // supplier opens on the SUPPLIER route: PartyDetailPage reads the path to
    // decide which list "back" returns to, and a supplier sent to the customers
    // route lands on a list it is not on. A 'both' party is offered under each.
    if (q && records) {
      const wantCustomers = scope === 'all' || scope === 'customers';
      const wantSuppliers = scope === 'all' || scope === 'suppliers';
      const hit = records.parties.filter((x) => matches(q, x.displayName, x.code));
      if (wantCustomers) {
        for (const p of hit.filter((x) => x.partyType !== 'vendor').slice(0, HIT_LIMIT)) {
          out.push({ id: `cust:${p.id}`, group: t('palette.customers', { defaultValue: 'Customers' }), label: p.displayName, sublabel: p.code, icon: Users, to: `/sales/parties/${p.id}` });
        }
      }
      if (wantSuppliers) {
        for (const p of hit.filter((x) => x.partyType === 'vendor' || x.partyType === 'both').slice(0, HIT_LIMIT)) {
          out.push({ id: `supp:${p.id}`, group: t('palette.suppliers', { defaultValue: 'Suppliers' }), label: p.displayName, sublabel: p.code, icon: Truck, to: `/purchasing/suppliers/${p.id}` });
        }
      }
    }

    // ...and whatever the server matched for this query.
    if (q && hits) {
      for (const it of hits.items) {
        out.push({ id: `item:${it.id}`, group: t('palette.items', { defaultValue: 'Items' }), label: it.name, sublabel: it.code, icon: Package, to: `/inventory/items/${it.id}` });
      }
      for (const src of DOC_SOURCES) {
        const rows = (hits[src.key] ?? []) as DocLite[];
        for (const d of rows) {
          // Whose document it is, and for how much — a column of bare numbers
          // tells you nothing about which one you wanted. Payments carry
          // `amount`, every other type carries `total`.
          const value = d.total ?? d.amount;
          const money = value && d.currencyCode
            ? formatMoney(value, { locale: i18n.language, currency: d.currencyCode })
            : null;
          // A credit/debit note IS its own kind of paper — saying so beats
          // listing it as an ordinary invoice with a quietly negative total.
          const note = d.isCreditNote || d.isDebitNote
            ? t('palette.note', { defaultValue: 'Note' })
            : null;
          out.push({
            id: `${src.key}:${d.id}`,
            group: t(src.labelKey, { defaultValue: src.fallback }),
            label: d.docNumber,
            sublabel: [note, d.partyName, money].filter(Boolean).join(' · ') || undefined,
            icon: src.icon,
            to: 'print' in src && src.print ? `${src.base}/${d.id}/print` : `${src.base}/${d.id}`,
          });
        }
      }
    }
    return out;
  }, [query, scope, permissions, enabledModules, records, hits, t, i18n.language]);

  /**
   * The chips, in the order a shop reaches for them. Built from the same
   * permission + module gates the results are, so a chip can never promise a
   * kind of record this user would get nothing back for.
   */
  const scopes = useMemo<Array<{ key: ScopeKey; label: string; icon: Icon }>>(() => {
    const out: Array<{ key: ScopeKey; label: string; icon: Icon }> = [
      { key: 'all', label: t('palette.scope.all', { defaultValue: 'All' }), icon: Search },
    ];
    for (const src of DOC_SOURCES) {
      if (!can(src.perm) || !modOn('module' in src ? src.module : undefined)) continue;
      out.push({ key: src.key, label: t(src.labelKey, { defaultValue: src.fallback }), icon: src.icon });
    }
    if (can('parties.read')) {
      out.push({ key: 'customers', label: t('palette.customers', { defaultValue: 'Customers' }), icon: Users });
      out.push({ key: 'suppliers', label: t('palette.suppliers', { defaultValue: 'Suppliers' }), icon: Truck });
    }
    if (can('items.read')) out.push({ key: 'items', label: t('palette.items', { defaultValue: 'Items' }), icon: Package });
    out.push({ key: 'pages', label: t('palette.pages', { defaultValue: 'Pages' }), icon: FileText });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, enabledModules, t]);

  const clampedActive = Math.min(active, Math.max(0, commands.length - 1));

  const run = (cmd: Cmd | undefined): void => {
    if (!cmd) return;
    onOpenChange(false);
    navigate(cmd.to);
  };

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(commands.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(commands[clampedActive]); }
    else if (e.key === 'Escape') { e.preventDefault(); onOpenChange(false); }
  };

  // Render groups in first-seen order, tracking a flat index for keyboard nav.
  const groups: Array<{ name: string; items: Array<{ cmd: Cmd; index: number }> }> = [];
  commands.forEach((cmd, index) => {
    let g = groups.find((x) => x.name === cmd.group);
    if (!g) { g = { name: cmd.group, items: [] }; groups.push(g); }
    g.items.push({ cmd, index });
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
      role="presentation"
    >
      <div
        className="glass-panel flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title', { defaultValue: 'Command palette' })}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 flex-none text-fg-muted" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            placeholder={t('palette.placeholder', { defaultValue: 'Search pages, actions, customers, items…' })}
            className="h-12 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-muted"
            aria-label={t('palette.placeholder', { defaultValue: 'Search' })}
          />
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-fg-subtle sm:inline">Esc</kbd>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto py-2">
          {commands.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-fg-muted">{t('palette.noResults', { defaultValue: 'No results' })}</p>
          ) : (
            groups.map((g) => (
              <div key={g.name} className="px-2 pb-1">
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-fg-subtle">{g.name}</div>
                {g.items.map(({ cmd, index }) => {
                  const Icon = cmd.icon;
                  const isActive = index === clampedActive;
                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      onMouseMove={() => setActive(index)}
                      onClick={() => run(cmd)}
                      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-start text-sm transition-colors ${isActive ? 'bg-brand/10 text-fg' : 'text-fg-muted hover:bg-bg-subtle'}`}
                    >
                      <span className={`grid h-7 w-7 flex-none place-items-center rounded-md ${isActive ? 'bg-brand/15 text-brand' : 'bg-bg-subtle text-fg-muted'}`}>
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="flex-1 truncate font-medium text-fg">{cmd.label}</span>
                      {cmd.sublabel && <span className="truncate text-xs text-fg-subtle">{cmd.sublabel}</span>}
                      {isActive && <ArrowRight className="h-3.5 w-3.5 flex-none text-brand rtl:-scale-x-100" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Narrow the search to one kind of record. Along the bottom because
            this is a refinement, not the first thing you reach for: you type,
            then narrow. Each chip is hidden unless its records are actually
            reachable, so the row shows what THIS shop and THIS user have. */}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-3 py-2">
          {scopes.map((sc) => {
            const on = scope === sc.key;
            const Icon = sc.icon;
            return (
              <button
                key={sc.key}
                type="button"
                onMouseDown={(e) => e.preventDefault()} /* keep focus in the input */
                onClick={() => { setScope(sc.key); setActive(0); inputRef.current?.focus(); }}
                aria-pressed={on}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${on ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:bg-bg-subtle hover:text-fg'}`}
              >
                <Icon className="h-3 w-3" aria-hidden />
                {sc.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-2 text-[11px] text-fg-subtle">
          <span className="inline-flex items-center gap-1"><kbd className="rounded border border-border px-1">↑</kbd><kbd className="rounded border border-border px-1">↓</kbd> {t('palette.navigate', { defaultValue: 'navigate' })}</span>
          <span className="inline-flex items-center gap-1"><kbd className="rounded border border-border px-1">↵</kbd> {t('palette.open', { defaultValue: 'open' })}</span>
          <FileText className="ms-auto h-3.5 w-3.5" aria-hidden />
        </div>
      </div>
    </div>
  );
}
